/**
 * Shared motion settings, from hung-hola's assets/js/motion.js (owner, 2026-10-20: "one shared variable, not one per
 * block"). CSS transitions take their timings from the tokens in assets/css/index.css; JS motion takes them from here.
 *
 * GSAP + ScrollTrigger are Nectar Blocks' (script handle "gsap", 3.12.7, both in one bundle, on window): never bundled
 * here, the `gsap` npm package is a dev dependency for its types only. A block that animates with GSAP lists the handle
 * before its own file: "viewScript": ["gsap", "file:./view.js"], and reads it with motionLib() (null without Nectar,
 * with ?still or reduced motion: the block then shows its still layout).
 */
import type { gsap as Gsap } from "gsap";
import type { ScrollTrigger as ScrollTriggerClass } from "gsap/ScrollTrigger";
import type Lenis from "lenis";
import type { LoopMotion } from "./loop-motion";

declare global {
  interface Window {
    gsap?: typeof Gsap;
    ScrollTrigger?: typeof ScrollTriggerClass;
    lenis?: Lenis;
    pxLoop?: LoopMotion;
  }
}

export const MOTION = {
  // Things appearing (shared fade-in), measured on the owner's reference growtrics.ai.
  // spread: s between two rings of a centre-out reveal (data-px-fade-in-item="center", services cards)
  reveal: {
    duration: 0.6,
    stagger: 0.08,
    rise: 28,
    ease: "power3.out",
    spread: 0.15,
  },
  // Endless strips and rings (testimonials, awards, partners, team; not discover): assets/js/loop-motion.ts
  loop: {
    hover: 0.25, // speed while the pointer is over the strip (1 = normal; slows down, never stops)
    scroll: 0.004, // extra speed per px/s of page scroll (either way), in the strip's own direction (not testimonials)
    maxBoost: 6, // cap of that extra speed
    settle: 0.12, // per frame at 60 fps: how fast the speed glides back to normal (or to the hover speed)
  },
  // Sliders with controls that autoplay (portfolio mobile; services and blog have none, owner 2026-10-20)
  slider: { autoplay: 3000 }, // ms per slide
  // Numbers counting up from 0 when they appear (Stats block, owner 2026-10-30; no Figma motion: chosen, change here)
  count: { duration: 1.6, ease: "power2.out" }, // s per number; they start MOTION.reveal.stagger apart, once on screen
  // The Portfolio Trail playing by itself while no mouse moves over the hero (owner 2026-10-30; no Figma motion: chosen)
  trail: {
    speed: 280, // px/s along its path (an ellipse on the empty side of the hero), slow (owner 2026-10-30)
    spacing: 45, // px of that path between two photos (a mouse: 40)
    hold: 1, // s a photo stays before it leaves (a mouse: 0.5): about ten on the ring at once
    idle: 1, // s after the mouse last moved over the hero before it plays again
    delay: 0.1, // s after the page loads before the first photo
  },
};

/** ?still (screenshot compare) or prefers-reduced-motion: nothing moves. */
export const isStill = (): boolean =>
  new URLSearchParams(window.location.search).has("still") ||
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * WebGL drawn on the CPU (no GPU: PageSpeed's servers, VMs, hardware acceleration off): a shader background there costs
 * ~200 ms a frame, so it shows its still look (plan 005, owner 2026-10-21: still only where the motion can't keep up).
 * Asked in a worker: the first WebGL context on the CPU takes seconds to start (SwiftShader: ~2 s on a throttled
 * phone), which would block the page. Software: no context, or a renderer named like one ("major performance caveat"
 * does not fail on headless Chrome's SwiftShader).
 */
const SOFTWARE = /SwiftShader|llvmpipe|softpipe|Software|Basic Render/i;
// the renderer's name, '' without WebGL2
const renderer = (gl: WebGL2RenderingContext | null): string => {
  const info = gl?.getExtension("WEBGL_debug_renderer_info");
  const name = gl
    ? String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER))
    : "";
  gl?.getExtension("WEBGL_lose_context")?.loseContext();
  return name;
};
// the same in the worker; null: no WebGL2 there (Safari before 17), asked on the page then
const WORKER = `const gl = new OffscreenCanvas(1, 1).getContext('webgl2');
const info = gl && gl.getExtension('WEBGL_debug_renderer_info');
postMessage(gl ? String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER)) : null);`;
let software: Promise<boolean> | undefined;
export function softwareWebGL(): Promise<boolean> {
  if (!software) {
    software = new Promise((resolve) => {
      const answer = (name: string): void =>
        resolve(!name || SOFTWARE.test(name));
      const onPage = (): void =>
        answer(renderer(document.createElement("canvas").getContext("webgl2")));
      if (!("OffscreenCanvas" in window) || !("Worker" in window)) {
        onPage();
        return;
      }
      const url = URL.createObjectURL(
        new Blob([WORKER], { type: "text/javascript" }),
      );
      const worker = new Worker(url);
      const end = (): void => {
        worker.terminate();
        URL.revokeObjectURL(url);
      };
      worker.onmessage = (e) => {
        end();
        if (typeof e.data === "string") {
          answer(e.data);
        } else {
          onPage();
        }
      };
      // a worker that can't run (CSP, no OffscreenCanvas in workers): ask on the page
      worker.onerror = () => {
        end();
        onPage();
      };
    });
  }
  return software;
}

/** Nectar's GSAP + ScrollTrigger, registered; null when either is missing or motion is off. */
export function motionLib(): {
  gsap: typeof Gsap;
  ScrollTrigger: typeof ScrollTriggerClass;
} | null {
  const { gsap, ScrollTrigger } = window;
  if (!gsap || !ScrollTrigger || isStill()) {
    return null;
  }
  gsap.registerPlugin(ScrollTrigger);
  return { gsap, ScrollTrigger };
}
